/**
 * MillageRedistributionOptimizer - Level 3 Optimization Component
 *
 * RESPONSIBILITY:
 * Reforms millage shape to maximize revenue utilization in debt-heavy scenarios
 * by reducing excess revenue through millage adjustments.
 *
 * HIERARCHY:
 * - Used by: ConfigurationOptimizer (Level 3)
 * - Uses: CandidateProcessor for full processing
 * - Peers: UtilizationOptimizer, FiveYearRuleOptimizer
 *
 * KEY OPERATIONS:
 * - detectOptimizationNeed: Analyze if utilization is <99%
 * - analyzeRevenue: Plan revenue reduction strategy
 * - constructNewShape: Build reformed millage shape
 * - processWithNewShape: Create new candidate with custom shape
 * - evaluateResults: Measure utilization improvement
 *
 * IMPLEMENTATION NOTES:
 * - Works backwards through tax years
 * - Respects fixed millage years
 * - Maintains decreasing millage pattern
 * - Uses 5% interest rate approximation
 */

import { CandidateNaming } from '~/engine/processing/shared/candidate-naming';
import { logger } from '~/services/logging';
import type { Candidate, Configuration, Project } from '~/types';
import { CandidateProcessor } from '../candidate-processor';

export interface MillageRedistributionConfig {
  minPrincipalBuffer: number; // Default: $15,000
  revenueReductionTarget: number; // Default: 0.70 (70%)
  maxIterations: number; // Default: 5
  interestRateAssumption: number; // Default: 0.05 (5%)
}

export interface OptimizationNeedAnalysis {
  shouldOptimize: boolean;
  utilizationPercentage: number;
  unusedRevenue: number;
  drfEndingBalance: number;
  reason: string;
}

export interface RevenueAnalysis {
  excessRevenue: number;
  targetReduction: number;
  flexibleYears: number[];
  fixedYears: number[];
  peakYears: number[];
  averageReductionPerYear: number;
}

export interface YearAdjustment {
  year: number;
  originalMillage: number;
  newMillage: number;
  revenueRemoved: number;
  millageReduction: number;
  accumulatedInterest: number;
}

export interface ShapeAdjustmentTracker {
  newMillageShape: number[];
  totalRevenuePulled: number;
  accumulatedInterestPulled: number;
  adjustmentsByYear: Map<number, YearAdjustment>;
}

export interface YearOptimizationData {
  year: number;
  millage: number;
  isFixed: boolean;
  taxableValue: number;
  totalRevenue: number;
  existingDebt: number;
  totalBondPayments: number;
  trueMinimum: number;
  accumulatedInterest: number;
}

export class MillageRedistributionOptimizer {
  private static readonly DEFAULT_CONFIG: MillageRedistributionConfig = {
    minPrincipalBuffer: 15000,
    revenueReductionTarget: 0.7,
    maxIterations: 5,
    interestRateAssumption: 0.05,
  };

  /**
   * Step 1: Detect if optimization is needed
   * Returns analysis of whether candidate needs millage redistribution
   */
  public static detectOptimizationNeed(candidate: Candidate): OptimizationNeedAnalysis {
    // Get DRF balances
    const drfStartingBalance = candidate.cashFlows?.[0]?.drfStartingBalance || 0;
    const drfEndingBalance =
      candidate.cashFlows?.[candidate.cashFlows.length - 1]?.drfEndingBalance || 0;

    // Calculate unused revenue (excess DRF at end)
    const unusedRevenue = drfEndingBalance - drfStartingBalance;

    // Get total revenue generated
    const totalRevenue = candidate.results?.totalRevenue || 0;

    // Calculate true utilization: What percentage of revenue was actually used?
    // If we end with excess DRF, that means we didn't use all our revenue
    let utilizationPercentage: number;
    if (totalRevenue > 0) {
      // If we have unused revenue, calculate what % was actually utilized
      if (unusedRevenue > 0) {
        utilizationPercentage = ((totalRevenue - unusedRevenue) / totalRevenue) * 100;
      } else {
        // If DRF decreased or stayed same, we used 100% or more
        utilizationPercentage = 100;
      }
    } else {
      utilizationPercentage = 0;
    }

    const shouldOptimize = utilizationPercentage < 99 && unusedRevenue > 100000; // Only optimize if >$100k unused

    const decision = shouldOptimize ? 'OPTIMIZE' : 'SKIP';
    const reason = shouldOptimize
      ? `Utilization ${utilizationPercentage.toFixed(2)}% is below 99% threshold with $${unusedRevenue.toLocaleString()} unused`
      : unusedRevenue > 100000
        ? `Utilization ${utilizationPercentage.toFixed(2)}% meets or exceeds 99% threshold`
        : `Unused revenue $${unusedRevenue.toLocaleString()} is below $100,000 threshold`;

    // Log optimization decision with key metrics
    logger.L3.info(`Millage redistribution analysis`, {
      candidate: candidate.name,
      decision,
      utilizationPercent: utilizationPercentage,
      unusedRevenue,
      drfEndingBalance,
    });

    if (shouldOptimize) {
      logger.domain.optimization(`Millage redistribution needed`, {
        candidate: candidate.name,
        currentUtilization: utilizationPercentage,
        excessRevenue: unusedRevenue,
      });
    }

    return {
      shouldOptimize,
      utilizationPercentage,
      unusedRevenue,
      drfEndingBalance,
      reason,
    };
  }

  /**
   * Helper: Build optimization data table for shape construction
   * Merges tax year plan and revenue data for processing
   */
  private static buildOptimizationTable(
    candidate: Candidate,
    revenueAnalysis: RevenueAnalysis,
    project: Project
  ): YearOptimizationData[] {
    const table: YearOptimizationData[] = [];
    const millageMap = candidate.millageInformation.millageMap;
    const taxYearPlan = candidate.taxYearBasedProjectPlan || [];
    // Get minimum millage from the millage map
    const allMillages = Object.values(millageMap).filter((m) => m > 0);
    const minimumMillage = allMillages.length > 0 ? Math.min(...allMillages) : 0;

    // Build table for each year in the millage map
    Object.entries(millageMap).forEach(([yearStr, millage]) => {
      const year = parseInt(yearStr, 10);
      // Get taxable value from project
      const taxableValueMap = project.taxableValueMap || {};
      const taxableValueEntry = taxableValueMap[year];
      const taxableValue = taxableValueEntry?.taxableValue || 0;
      const totalRevenue = (millage / 1000) * taxableValue;

      // Get existing debt for this year from tax year plan
      const existingDebt = taxYearPlan.find((y) => y.taxYear === year)?.existingDebt || 0;

      // Calculate total bond payments for this year from cashflows
      // Use relatedTaxBillYear to match tax years
      const cashFlowsForYear =
        candidate.cashFlows?.filter((flow) => flow.relatedTaxBillYear === year) || [];
      // Sum up bond payments for this tax year
      const totalBondPayments = cashFlowsForYear.reduce(
        (sum, flow) => sum + (flow.totalBondPayment || 0),
        0
      );

      // Determine if year is fixed (peak or minimum)
      const isFixed =
        revenueAnalysis.peakYears.includes(year) ||
        revenueAnalysis.fixedYears.includes(year) ||
        millage <= minimumMillage;

      // Calculate true minimum (max of minimum millage and next year's millage)
      // Need to maintain decreasing pattern after peak
      let trueMinimum = minimumMillage;
      // Get next year's millage if it exists
      const nextYear = year + 1;
      if (millageMap[nextYear] !== undefined) {
        trueMinimum = Math.max(minimumMillage, millageMap[nextYear]);
      }

      table.push({
        year,
        millage,
        isFixed,
        taxableValue,
        totalRevenue,
        existingDebt,
        totalBondPayments,
        trueMinimum,
        accumulatedInterest: 0, // Will be updated during processing
      });
    });

    return table;
  }

  /**
   * Step 2: Analyze revenue and plan reduction strategy
   */
  public static analyzeRevenue(
    candidate: Candidate,
    _project: Project,
    config: Partial<MillageRedistributionConfig> = {}
  ): RevenueAnalysis {
    const mergedConfig = { ...MillageRedistributionOptimizer.DEFAULT_CONFIG, ...config };

    // Get millage values from millageMap instead of intervals
    // The millageMap contains the actual millage rates by tax year
    const millageMap = candidate.millageInformation.millageMap;
    const millageValues = Object.values(millageMap).filter((m) => m > 0);
    const uniqueMillages = [...new Set(millageValues)];
    const minimumMillage = Math.min(...uniqueMillages);
    const peakMillage = Math.max(...uniqueMillages);

    // Identify year types
    const fixedYears: number[] = [];
    const flexibleYears: number[] = [];
    const peakYears: number[] = [];

    // Classify years based on their millage values
    Object.entries(millageMap).forEach(([yearStr, millage]) => {
      const year = parseInt(yearStr, 10);

      if (millage === peakMillage) {
        // At peak - these stay fixed at peak
        peakYears.push(year);
      } else if (millage === minimumMillage) {
        // At minimum - can't reduce further
        fixedYears.push(year);
      } else if (millage > minimumMillage && millage < peakMillage) {
        // Between minimum and peak - these are flexible
        flexibleYears.push(year);
      }
    });

    // Calculate excess revenue
    const drfEndingBalance =
      candidate.cashFlows?.[candidate.cashFlows.length - 1]?.drfEndingBalance || 0;
    const drfStartingBalance = candidate.cashFlows?.[0]?.drfStartingBalance || 0;
    const excessRevenue = drfEndingBalance - drfStartingBalance;

    // Calculate target reduction
    const targetReduction = excessRevenue * mergedConfig.revenueReductionTarget;
    const averageReductionPerYear =
      flexibleYears.length > 0 ? targetReduction / flexibleYears.length : 0;

    // Log revenue analysis results
    logger.L3.debug(`Revenue analysis completed`, {
      excessRevenue,
      targetReduction,
      flexibleYearsCount: flexibleYears.length,
      peakYearsCount: peakYears.length,
      fixedYearsCount: fixedYears.length,
      avgReductionPerYear: averageReductionPerYear,
    });

    return {
      excessRevenue,
      targetReduction,
      flexibleYears,
      fixedYears,
      peakYears,
      averageReductionPerYear,
    };
  }

  /**
   * Step 3: Construct new millage shape using reverse processing
   * Works backwards through years to reduce revenue
   */
  private static constructNewShape(
    candidate: Candidate,
    revenueAnalysis: RevenueAnalysis,
    project: Project,
    config: Partial<MillageRedistributionConfig> = {}
  ): ShapeAdjustmentTracker {
    const mergedConfig = { ...MillageRedistributionOptimizer.DEFAULT_CONFIG, ...config };

    // Build optimization table
    const optimizationTable = MillageRedistributionOptimizer.buildOptimizationTable(
      candidate,
      revenueAnalysis,
      project
    );

    // Initialize tracking
    const tracker: ShapeAdjustmentTracker = {
      newMillageShape: [],
      totalRevenuePulled: 0,
      accumulatedInterestPulled: 0,
      adjustmentsByYear: new Map(),
    };

    // Process years in reverse order
    let accumulatedInterest = 0;
    const targetReduction = revenueAnalysis.targetReduction;

    // Process each year from last to first
    for (let i = optimizationTable.length - 1; i >= 0; i--) {
      const yearData = optimizationTable[i];

      // Update accumulated interest for this year
      yearData.accumulatedInterest = accumulatedInterest;

      // Skip fixed years
      if (yearData.isFixed) {
        tracker.newMillageShape.unshift(yearData.millage);
        logger.L3.trace(`Fixed year skipped`, {
          year: yearData.year,
          millage: yearData.millage,
        });
        continue;
      }

      // Calculate available revenue to pull
      const availableCapacity = yearData.totalRevenue - yearData.existingDebt - accumulatedInterest;

      // Ensure minimum principal buffer
      const maxPullable = Math.max(0, availableCapacity - mergedConfig.minPrincipalBuffer);

      // Don't pull more than we need
      const remainingTarget = targetReduction - tracker.totalRevenuePulled;
      const actualPull = Math.min(maxPullable, remainingTarget);

      // Calculate new millage
      const newRevenue = yearData.totalRevenue - actualPull;
      const newMillage =
        yearData.taxableValue > 0 ? (newRevenue / yearData.taxableValue) * 1000 : yearData.millage;

      // Ensure new millage respects true minimum
      const finalMillage = Math.max(newMillage, yearData.trueMinimum);

      // Recalculate actual pull based on final millage
      const finalRevenue = (finalMillage / 1000) * yearData.taxableValue;
      const actualRevenuePulled = yearData.totalRevenue - finalRevenue;

      // Update tracking
      tracker.newMillageShape.unshift(finalMillage);
      tracker.totalRevenuePulled += actualRevenuePulled;

      // Calculate interest impact (approximate)
      const interestImpact = yearData.totalBondPayments * mergedConfig.interestRateAssumption;
      accumulatedInterest += interestImpact;
      tracker.accumulatedInterestPulled += interestImpact;

      // Store year adjustment
      const adjustment: YearAdjustment = {
        year: yearData.year,
        originalMillage: yearData.millage,
        newMillage: finalMillage,
        revenueRemoved: actualRevenuePulled,
        millageReduction: yearData.millage - finalMillage,
        accumulatedInterest: accumulatedInterest,
      };
      tracker.adjustmentsByYear.set(yearData.year, adjustment);
    }

    // Log shape construction results
    logger.L3.info(`Millage shape reformed`, {
      totalRevenuePulled: tracker.totalRevenuePulled,
      targetReduction,
      percentOfTarget: ((tracker.totalRevenuePulled / targetReduction) * 100).toFixed(1),
      adjustedYears: tracker.adjustmentsByYear.size,
    });

    return tracker;
  }

  /**
   * Main optimization entry point
   * Orchestrates the full optimization process
   */
  public static optimize(
    candidate: Candidate,
    project: Project,
    configuration: Configuration,
    config: Partial<MillageRedistributionConfig> = {}
  ): { success: boolean; optimizedCandidate?: Candidate; message: string } {
    // Step 1: Check if optimization is needed
    const needAnalysis = MillageRedistributionOptimizer.detectOptimizationNeed(candidate);

    if (!needAnalysis.shouldOptimize) {
      return {
        success: false,
        message: needAnalysis.reason,
      };
    }

    // Step 2: Analyze revenue (using let for potential reassignment in loop)
    let revenueAnalysis = MillageRedistributionOptimizer.analyzeRevenue(candidate, project, config);

    if (revenueAnalysis.flexibleYears.length === 0) {
      return {
        success: false,
        message: 'No flexible years available for adjustment',
      };
    }

    // Initialize iteration tracking
    const mergedConfig = { ...MillageRedistributionOptimizer.DEFAULT_CONFIG, ...config };
    let currentCandidate = candidate;
    let bestCandidate = candidate;
    let bestUtilization = needAnalysis.utilizationPercentage;
    let iterationNumber = 0;
    const allCandidates: Candidate[] = [candidate];

    // Iterate until we reach target utilization or max iterations
    while (iterationNumber < mergedConfig.maxIterations) {
      iterationNumber++;

      // Adjust parameters for this iteration
      const iterationConfig = MillageRedistributionOptimizer.adjustParametersForIteration(
        iterationNumber,
        revenueAnalysis,
        mergedConfig
      );

      // Step 3: Construct new shape with adjusted parameters
      const shapeTracker = MillageRedistributionOptimizer.constructNewShape(
        currentCandidate,
        revenueAnalysis,
        project,
        iterationConfig
      );

      if (shapeTracker.totalRevenuePulled === 0) {
        logger.L3.warn(`Optimization iteration stopped`, {
          iteration: iterationNumber,
          reason: 'No revenue could be pulled',
        });
        break;
      }

      // Step 4: Process with new shape
      const optimizedCandidate = MillageRedistributionOptimizer.processWithNewShape(
        currentCandidate,
        shapeTracker,
        project,
        configuration
      );

      if (!optimizedCandidate) {
        logger.L3.warn(`Optimization iteration stopped`, {
          iteration: iterationNumber,
          reason: 'Failed to create candidate',
        });
        break;
      }

      allCandidates.push(optimizedCandidate);

      // Step 5: Evaluate results
      const evaluation = MillageRedistributionOptimizer.evaluateResults(
        currentCandidate,
        optimizedCandidate,
        iterationNumber
      );

      if (evaluation.newUtilization > bestUtilization) {
        bestCandidate = optimizedCandidate;
        bestUtilization = evaluation.newUtilization;
      }

      // Step 6: Check if we should continue iterating
      if (evaluation.newUtilization >= 99) {
        logger.L3.info(`Optimization target achieved`, {
          iteration: iterationNumber,
          utilization: evaluation.newUtilization,
          target: 99,
        });
        logger.domain.optimization(`Target utilization reached`, {
          finalUtilization: evaluation.newUtilization,
        });
        break;
      }

      if (!evaluation.improved) {
        logger.L3.debug(`Optimization iteration stopped`, {
          iteration: iterationNumber,
          reason: 'No improvement',
        });
        break;
      }

      // Prepare for next iteration
      currentCandidate = optimizedCandidate;

      // Recalculate revenue analysis for the new candidate
      revenueAnalysis = MillageRedistributionOptimizer.analyzeRevenue(
        optimizedCandidate,
        project,
        iterationConfig
      );
    }

    return {
      success: bestUtilization > needAnalysis.utilizationPercentage,
      optimizedCandidate: bestCandidate,
      message: `Optimization complete after ${iterationNumber} iterations. Utilization improved from ${needAnalysis.utilizationPercentage.toFixed(2)}% to ${bestUtilization.toFixed(2)}%`,
    };
  }

  /**
   * Step 5: Evaluate optimization results
   * Compares the optimized candidate against the original
   */
  private static evaluateResults(
    originalCandidate: Candidate,
    optimizedCandidate: Candidate,
    iterationNumber: number
  ): { newUtilization: number; improved: boolean; improvementPercent: number } {
    // Calculate new utilization
    const drfStart = optimizedCandidate.cashFlows?.[0]?.drfStartingBalance || 0;
    const drfEnd =
      optimizedCandidate.cashFlows?.[optimizedCandidate.cashFlows.length - 1]?.drfEndingBalance ||
      0;
    const unusedRevenue = drfEnd - drfStart;
    const totalRevenue = optimizedCandidate.results?.totalRevenue || 0;

    let newUtilization = 100;
    if (totalRevenue > 0 && unusedRevenue > 0) {
      newUtilization = ((totalRevenue - unusedRevenue) / totalRevenue) * 100;
    }

    // Calculate original utilization
    const origDrfStart = originalCandidate.cashFlows?.[0]?.drfStartingBalance || 0;
    const origDrfEnd =
      originalCandidate.cashFlows?.[originalCandidate.cashFlows.length - 1]?.drfEndingBalance || 0;
    const origUnusedRevenue = origDrfEnd - origDrfStart;
    const origTotalRevenue = originalCandidate.results?.totalRevenue || 0;

    let originalUtilization = 100;
    if (origTotalRevenue > 0 && origUnusedRevenue > 0) {
      originalUtilization = ((origTotalRevenue - origUnusedRevenue) / origTotalRevenue) * 100;
    }

    const improved = newUtilization > originalUtilization;
    const improvementPercent = newUtilization - originalUtilization;

    // Log iteration results
    logger.L3.info(`Optimization iteration results`, {
      iteration: iterationNumber,
      originalUtilization,
      newUtilization,
      improvement: improvementPercent,
      improved,
      viable: optimizedCandidate.results?.isViable || false,
    });

    if (improved) {
      logger.domain.optimization(`Utilization improved`, {
        from: originalUtilization,
        to: newUtilization,
        improvement: improvementPercent,
      });
    }

    return {
      newUtilization,
      improved,
      improvementPercent,
    };
  }

  /**
   * Adjust optimization parameters for next iteration
   * Makes the optimization more or less aggressive based on previous results
   */
  private static adjustParametersForIteration(
    iterationNumber: number,
    _revenueAnalysis: RevenueAnalysis,
    baseConfig: MillageRedistributionConfig
  ): MillageRedistributionConfig {
    // Start conservative and get more aggressive with each iteration
    // Iteration 1: 70% reduction
    // Iteration 2: 80% reduction
    // Iteration 3: 90% reduction
    // Iteration 4+: 95% reduction

    let reductionTarget = baseConfig.revenueReductionTarget;
    if (iterationNumber === 2) {
      reductionTarget = 0.8;
    } else if (iterationNumber === 3) {
      reductionTarget = 0.9;
    } else if (iterationNumber >= 4) {
      reductionTarget = 0.95;
    }

    // Also reduce buffer in later iterations if needed
    let buffer = baseConfig.minPrincipalBuffer;
    if (iterationNumber >= 3) {
      buffer = buffer * 0.5; // Reduce to $7,500 in iteration 3+
    }
    if (iterationNumber >= 4) {
      buffer = buffer * 0.5; // Reduce to $3,750 in iteration 4+
    }

    return {
      ...baseConfig,
      revenueReductionTarget: reductionTarget,
      minPrincipalBuffer: buffer,
    };
  }

  /**
   * Step 4: Process candidate with new millage shape
   * Creates a new candidate with the adjusted millage shape
   */
  private static processWithNewShape(
    originalCandidate: Candidate,
    shapeTracker: ShapeAdjustmentTracker,
    project: Project,
    configuration: Configuration
  ): Candidate | null {
    try {
      const startTime = Date.now();

      // Count actual optimizations (excluding initial-shape and optimization-skipped)
      const appliedOptCount =
        originalCandidate.optimizationHistory?.filter(
          (h) => h.type !== 'initial-shape' && h.type !== 'optimization-skipped'
        ).length || 0;
      const newOptCount = appliedOptCount + 1;

      // Use centralized naming: {shapeId}.{count}
      const shapeId = CandidateNaming.getShapeId(originalCandidate.name);
      const newName = CandidateNaming.createOptimizationName(originalCandidate.name, newOptCount);

      // Clone the original candidate
      const newCandidate: Candidate = {
        ...originalCandidate,
        id: `${shapeId}.${newOptCount}-${crypto.randomUUID().slice(0, 8)}`,
        name: newName,
        description: shapeId,
        isProcessed: false, // Will be processed
        revenueMap: {}, // Clear revenue map to force rebuild with new millage
        cashFlows: [], // Clear cash flows to force recalculation
        optimizationHistory: [
          ...(originalCandidate.optimizationHistory || []),
          {
            type: 'millage-adjustment' as const,
            description: `Millage shape reformed to reduce excess revenue by $${shapeTracker.totalRevenuePulled.toLocaleString()}`,
            timestamp: new Date().toISOString(),
            details: {
              principalMoved: shapeTracker.totalRevenuePulled,
              drfImpact: -shapeTracker.totalRevenuePulled,
            },
          },
        ],
      };

      // Update the millage shape with new values
      // Create new millageMap from the adjusted shape
      const years = Object.keys(originalCandidate.millageInformation.millageMap)
        .map(Number)
        .sort((a, b) => a - b);

      const newMillageMap: Record<number, number> = {};
      years.forEach((year, index) => {
        newMillageMap[year] = shapeTracker.newMillageShape[index];
      });

      // Also update the intervals in the millage shape
      const newIntervals = originalCandidate.millageInformation.millageShape.intervals.map(
        (interval, index) => {
          return {
            ...interval,
            startMillage: shapeTracker.newMillageShape[index],
            endMillage: shapeTracker.newMillageShape[index],
          };
        }
      );

      // Update millage information with both map and intervals
      // Keep the base shapeId for the millage shape (values testing uses this for comparison)
      newCandidate.millageInformation = {
        ...originalCandidate.millageInformation,
        millageMap: newMillageMap,
        millageShape: {
          ...originalCandidate.millageInformation.millageShape,
          shapeType: 'linear' as const, // Mark as custom/modified
          shapeId: shapeId, // Keep base shape ID for testing comparison
          shapeName: `${shapeId} (adjusted)`,
          intervals: newIntervals,
        },
      };

      // Process the candidate with the new shape
      const result = CandidateProcessor.processCandidate(newCandidate, project, configuration);

      const elapsedTime = Date.now() - startTime;
      const isViable = result.candidate.results?.isViable || false;

      // Log processing completion (debug level - per-candidate detail)
      logger.L3.debug(`Millage redistribution: processed candidate`, {
        name: newName,
        processingTime: elapsedTime,
        viable: isViable,
      });

      return result.candidate;
    } catch (error) {
      console.error(`[PROCESSING ERROR] Failed to process with new shape:`, error);
      return null;
    }
  }
}
